// What a viewport draws, by what it says of itself in `ready` (its API version, the sim formats and
// layers it lists) and by where a run sits: the bridge sends only what passes, and the HUD, the
// inspector, Methods and the framing of Neurons' close-up read the same rules.

import type { Command } from './api.ts';
import type { SimSlot } from '../app/store.ts';
import type { CellsHeader, CommonHeader, DynamicsHeader, SimFormat } from '../data/sims/types.ts';

/** The API minor each command arrived in (viewport-api-v0 history); unlisted commands are 0.1. */
const SINCE_MINOR: Partial<Record<Command['cmd'], number>> = { sim: 2, layer: 2, overlay: 2, glow: 3 };

/** Major and minor of a `ready.api` string: '0.2' → [0, 2]. */
export function parseApi(api: string | undefined): [number, number] {
  const [major, minor] = (api ?? '').split('.').map((n) => Number.parseInt(n, 10));
  return [Number.isFinite(major) ? major! : 0, Number.isFinite(minor) ? minor! : 0];
}

/** Whether a viewport speaking `api` takes a command. Before `ready`, everything is assumed. */
export function supports(api: string | undefined, cmd: Command['cmd']): boolean {
  const need = SINCE_MINOR[cmd];
  return api === undefined || need === undefined || parseApi(api)[1] >= need;
}

/** `layer` names from 0.3 (contract phases): cells-sim-v0's arbors, somata and synapses, and sim volumes and surfaces. */
const LAYER_0_3 = /^(arbors|somata|synapses)$|^(volume|surface):/;

/** The API minor a command needs: by command, and for `layer` by name. */
export function minorFor(cmd: Command): number {
  if (cmd.cmd === 'layer' && LAYER_0_3.test(cmd.name)) return 3;
  return SINCE_MINOR[cmd.cmd] ?? 1;
}

/** Whether a viewport speaking `api` takes this command, layer names included. Before `ready`, everything is assumed. */
export function accepts(api: string | undefined, cmd: Command): boolean {
  return api === undefined || parseApi(api)[1] >= minorFor(cmd);
}

/**
 * Whether the viewport draws a sim layer, by its `ready.layers` (0.3, #576): a name it lists, or one
 * under a prefix it lists (`volume:` covers `volume:tissue-state`). A viewport that doesn't list them
 * is taken to draw what its API version takes (`accepts`). Layers it doesn't draw aren't sent: it would
 * refuse them.
 */
export function drawsLayer(layers: readonly string[] | undefined, name: string): boolean {
  return !layers || layers.some((l) => (l.endsWith(':') ? name.startsWith(l) : name === l));
}

/** Sims need viewport API 0.2: false once a viewport has said it's older. */
export const drawsSims = (api: string | undefined) => supports(api, 'sim');

/**
 * The API minor each sim format draws from (viewport-api-v0 phases), for a viewport that doesn't list
 * its formats in `ready.sims`: a cells run's arbors, somata and synapses arrive in 0.3, and pharma
 * (whose layers are 0.3's surfaces; the 0.2 engine refuses its runs) with them.
 */
export const FORMAT_SINCE: Record<SimFormat, number> = { hemo: 2, dynamics: 2, pharma: 3, cells: 3 };

/** Whether a viewport speaking `api` draws sims of this format. Before `ready`, everything is assumed. */
export function drawsFormat(api: string | undefined, format: SimFormat): boolean {
  return api === undefined || parseApi(api)[1] >= FORMAT_SINCE[format];
}

/**
 * Whether the viewport draws this slot's run: by the formats its `ready.sims` lists (`header.format`)
 * when it sends that list, else by `FORMAT_SINCE`. A run in a format it doesn't read isn't sent: the
 * engine would refuse it with a console error.
 */
export function drawsSlot(api: string | undefined, sims: string[] | undefined, x: Pick<SimSlot, 'format' | 'header'>): boolean {
  if (api === undefined) return true;
  return sims ? sims.includes(x.header.format) : drawsFormat(api, x.format);
}

/**
 * Whether a viewport places surface runs on the brain it shows, through that brain's maps
 * (engines/wgpu #500). That came before `ready.sims` (#467), so a viewport that lists its sim formats
 * places them, and one that doesn't is taken not to.
 */
export const placesSurfaces = (sims: string[] | undefined) => sims !== undefined;

/**
 * The surface spaces whose runs play on the shown brain's cortex. The viewport says so in its `load`
 * reply (`maps`, engines/wgpu #541): a surface run goes only where that lists its space, and `[]` means
 * the brain places none. A viewport from before that doesn't say, which the contract reads as unknown,
 * so the app goes on as before: from one that places surface runs at all, the maps the shown brain's HD
 * cortex lists, within what its `ready.maps` can place where it lists that.
 */
export function brainMaps(reply: readonly string[] | undefined, shown: readonly string[] | undefined, sims: string[] | undefined, can: readonly string[] | undefined): readonly string[] | undefined {
  if (reply) return reply;
  if (!placesSurfaces(sims)) return undefined;
  return can ? shown?.filter((m) => can.includes(m)) : shown;
}

/** A run with its own cortical mesh (dynamics `arrays.vertices` and `triangles`): the kind the viewport moves onto the brain it shows through a map. */
export function surfaceRun(header: CommonHeader): boolean {
  const a = (header as DynamicsHeader).arrays;
  return header.format === 'ba-dynamics-sim-v0' && !!a?.vertices && !!a.triangles;
}

/**
 * Whether a run can sit in the loaded pack, by the viewport's rule (ba-sim `fit` and `fit_with`,
 * viewport-api 0.2 notes): its space is the pack's, or `non-spatial`, or it records a placement into
 * the pack's space, or it's a surface run on a mesh the shown brain has a map from (`maps`, given only
 * for a viewport that places surface runs). The viewport refuses any other `sim` with a console
 * error, whatever the format: the HUP fits are fsaverage parcels with no mesh, so they stay out.
 * Unknown spaces pass.
 */
export function placedIn(header: CommonHeader, packSpace: string | undefined, maps?: readonly string[]): boolean {
  if (!packSpace || !header.space || header.space === 'non-spatial') return true;
  if (header.space === packSpace || (header as CellsHeader).placement?.space === packSpace) return true;
  return !!maps?.includes(header.space) && surfaceRun(header);
}

/** A placed run that plays on the shown brain's cortex through a map, rather than in the pack's own space. */
export function onBrainCortex(header: CommonHeader, packSpace: string | undefined, maps?: readonly string[]): boolean {
  return !!packSpace && !!header.space && header.space !== packSpace && !!maps?.includes(header.space) && surfaceRun(header);
}
